import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
    Download, Plus, Copy, RefreshCw, Trash2, Loader2, ShieldAlert,
    BookOpen, Users, X as XIcon, ChevronRight, Globe, Trophy,
} from 'lucide-react';

const LAUNCH_LANGUAGES = [
    { slug: 'shona', label: 'Shona', flag: '🇿🇼' },
    { slug: 'ndebele', label: 'Ndebele', flag: '🇿🇼' },
    { slug: 'yoruba', label: 'Yoruba', flag: '🇳🇬' },
    { slug: 'swahili', label: 'Swahili', flag: '🇰🇪' },
    { slug: 'french', label: 'French', flag: '🇫🇷' },
    { slug: 'portuguese', label: 'Portuguese', flag: '🇵🇹' },
];
const MAX_LANGUAGES = 3;

export default function Teacher() {
    const { user } = useAuth();
    const [classes, setClasses] = useState([]);
    const [org, setOrg] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        if (!user) return;
        try {
            const { data } = await api.get('/teacher/overview');
            setClasses(data.classes || []);
            setOrg(data.organisation || null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not load classes');
        } finally { setLoading(false); }
    }, [user]);

    useEffect(() => { refresh(); }, [refresh]);

    const exportCsv = (cid, name) => {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/teacher/classes/${cid}/export.csv`;
        const token = localStorage.getItem('lq_token');
        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.blob())
            .then((blob) => {
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = `${name.replace(/\s+/g, '_')}.csv`;
                a.click();
            })
            .catch(() => toast.error('CSV download failed'));
    };

    if (!user) return <main className="max-w-4xl mx-auto px-6 py-16">Sign in as a teacher.</main>;
    if (user.role !== 'teacher' && user.role !== 'admin') return <main className="max-w-4xl mx-auto px-6 py-16">Teacher role required.</main>;

    const orgActive = org?.status === 'active';
    const noOrg = !org;

    return (
        <main className="max-w-7xl mx-auto px-6 py-10" data-testid="page-teacher">
            <p className="text-sm uppercase font-semibold tracking-wider text-lq-sky-600">Teacher</p>
            <h1 className="mt-1 font-display text-3xl md:text-4xl font-semibold text-lq-ink-black">Your classes</h1>

            {org && (
                <div className="mt-4 inline-flex items-center gap-2 bg-white border border-lq-ink-100 rounded-full px-4 py-1.5 text-sm" data-testid="teacher-org-pill">
                    <BookOpen size={14} className="text-lq-sky-600" />
                    <strong className="text-lq-ink-900">{org.name}</strong>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${orgActive ? 'bg-lq-success/10 text-lq-success' : 'bg-lq-warn/10 text-lq-warn'}`}>{org.status}</span>
                    <span className="text-xs text-lq-ink-500">{org.seats_used}/{org.seats_purchased} seats</span>
                </div>
            )}

            {noOrg && user.role === 'teacher' && (
                <div className="mt-6 bg-lq-warn/5 border border-lq-warn/30 rounded-2xl p-5 flex gap-3" data-testid="teacher-no-org">
                    <ShieldAlert className="text-lq-warn flex-shrink-0 mt-0.5" size={20} />
                    <div>
                        <p className="text-sm text-lq-ink-900 font-medium">Your account isn't linked to a school yet.</p>
                        <p className="text-sm text-lq-ink-700 mt-1">A school administrator must provision Linguistic Quest for your school before you can create classes. Email <a href="mailto:info@linguisticquest.com" className="text-lq-purple-700 hover:underline">info@linguisticquest.com</a> to get started.</p>
                    </div>
                </div>
            )}

            {!noOrg && !orgActive && (
                <div className="mt-6 bg-lq-warn/5 border border-lq-warn/30 rounded-2xl p-5 flex gap-3" data-testid="teacher-org-pending">
                    <ShieldAlert className="text-lq-warn flex-shrink-0 mt-0.5" size={20} />
                    <div>
                        <p className="text-sm text-lq-ink-900 font-medium">Your school subscription is pending payment.</p>
                        <p className="text-sm text-lq-ink-700 mt-1">You can create classes now, but students will only be able to join once the school's subscription is marked as active.</p>
                    </div>
                </div>
            )}

            {!noOrg && <NewClassForm onCreated={refresh} disabled={false} />}

            {loading && <p className="mt-6 text-lq-ink-500">Loading classes…</p>}

            {!loading && classes.length === 0 && !noOrg && (
                <p className="mt-6 text-lq-ink-500">No classes yet. Use the form above to create your first one.</p>
            )}

            <div className="mt-8 space-y-8">
                {classes.map((c) => (
                    <ClassCard key={c.id} c={c} onChange={refresh} exportCsv={exportCsv} />
                ))}
            </div>
        </main>
    );
}

function NewClassForm({ onCreated }) {
    const [name, setName] = useState('');
    const [grade, setGrade] = useState('');
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        setBusy(true);
        try {
            const { data } = await api.post('/teacher/classes', { name: name.trim(), grade_level: grade.trim() || null });
            toast.success(`Class "${data.class.name}" created — code ${data.class.join_code}`);
            setName(''); setGrade('');
            await onCreated?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not create class');
        } finally { setBusy(false); }
    };

    return (
        <form onSubmit={submit} className="mt-6 bg-white border border-lq-ink-100 rounded-2xl p-5" data-testid="teacher-new-class">
            <h2 className="font-display text-lg font-semibold text-lq-ink-900">Create a new class</h2>
            <p className="text-sm text-lq-ink-500 mt-1">Each class gets its own join code. Share it with your students; they sign in via /schools/join.</p>
            <div className="mt-4 grid sm:grid-cols-[2fr,1fr,auto] gap-3">
                <input value={name} onChange={(e) => setName(e.target.value)} required maxLength={80} placeholder="Class name (e.g. Year 6 French)" className="rounded-lg border border-lq-ink-300 px-3 py-2" data-testid="teacher-class-name" />
                <input value={grade} onChange={(e) => setGrade(e.target.value)} maxLength={40} placeholder="Year / grade (optional)" className="rounded-lg border border-lq-ink-300 px-3 py-2" data-testid="teacher-class-grade" />
                <button disabled={busy || !name.trim()} className="px-5 py-2 rounded-full bg-lq-sky-600 text-white font-semibold hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1" data-testid="teacher-class-submit">
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create class
                </button>
            </div>
        </form>
    );
}

function ClassCard({ c, onChange, exportCsv }) {
    const [busy, setBusy] = useState(false);
    const [showAdd, setShowAdd] = useState(false);
    const [progressStudent, setProgressStudent] = useState(null);

    const copyCode = () => {
        navigator.clipboard.writeText(c.join_code);
        toast.success(`Join code ${c.join_code} copied`);
    };
    const copyLink = () => {
        const link = `${window.location.origin}/schools/join?code=${encodeURIComponent(c.join_code)}`;
        navigator.clipboard.writeText(link);
        toast.success('Student join link copied');
    };
    const regen = async () => {
        if (!window.confirm('Regenerate the join code? Students using the old code will need the new one.')) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/teacher/classes/${c.id}/regenerate-code`);
            toast.success(`New code: ${data.join_code}`);
            await onChange?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not regenerate');
        } finally { setBusy(false); }
    };
    const removeStudent = async (s) => {
        if (!window.confirm(`Remove ${s.display_name} from this class? Their account is detached and the seat is freed.`)) return;
        try {
            await api.delete(`/teacher/classes/${c.id}/students/${s.id}`);
            toast.success(`${s.display_name} removed`);
            await onChange?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not remove');
        }
    };

    return (
        <section className="bg-white rounded-2xl border border-lq-ink-100 p-6" data-testid={`class-${c.id}`}>
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="min-w-0">
                    <h2 className="font-display text-2xl text-lq-ink-900">{c.name}</h2>
                    <p className="text-sm text-lq-ink-500">{c.grade_level || ''} · {c.student_count} student{c.student_count === 1 ? '' : 's'}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={() => setShowAdd((v) => !v)} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-lq-purple-500 text-white rounded-full hover:bg-lq-purple-700" data-testid={`add-student-toggle-${c.id}`}>
                        <Plus size={14} /> Add student
                    </button>
                    <button onClick={() => exportCsv(c.id, c.name)} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-lq-sky-100 text-lq-sky-600 rounded-full hover:bg-lq-sky-300" data-testid={`csv-${c.id}`}>
                        <Download size={14} /> CSV report
                    </button>
                </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2 bg-lq-paper border border-lq-ink-100 rounded-xl px-4 py-3">
                <span className="text-xs uppercase tracking-wide text-lq-ink-500">Join code</span>
                <strong className="font-mono text-lg text-lq-purple-700" data-testid={`class-${c.id}-code`}>{c.join_code}</strong>
                <button onClick={copyCode} className="text-xs px-2 py-1 rounded-full bg-white border border-lq-ink-300 text-lq-ink-900 hover:bg-lq-ink-100 inline-flex items-center gap-1" data-testid={`class-${c.id}-copy-code`}>
                    <Copy size={12} /> Copy code
                </button>
                <button onClick={copyLink} className="text-xs px-2 py-1 rounded-full bg-white border border-lq-ink-300 text-lq-ink-900 hover:bg-lq-ink-100 inline-flex items-center gap-1" data-testid={`class-${c.id}-copy-link`}>
                    <Copy size={12} /> Copy join link
                </button>
                <button onClick={regen} disabled={busy} className="text-xs px-2 py-1 rounded-full bg-white border border-lq-ink-300 text-lq-ink-900 hover:bg-lq-ink-100 inline-flex items-center gap-1 disabled:opacity-50" data-testid={`class-${c.id}-regen`}>
                    {busy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Regenerate
                </button>
            </div>

            {showAdd && (
                <AddStudentForm
                    classId={c.id}
                    onClose={() => setShowAdd(false)}
                    onCreated={async () => { setShowAdd(false); await onChange?.(); }}
                />
            )}

            <ClassLeaderboard classId={c.id} key={`lb-${c.id}-${c.student_count}`} />

            <div className="mt-5 bg-lq-paper border border-lq-ink-100 rounded-xl overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-lq-sky-100 text-lq-ink-900 text-xs uppercase tracking-wider">
                        <tr>
                            <th className="text-left px-4 py-2">Student</th>
                            <th className="text-left px-4 py-2">Username</th>
                            <th className="text-left px-4 py-2">Languages</th>
                            <th className="text-left px-4 py-2">Puzzles</th>
                            <th className="text-left px-4 py-2">Joined</th>
                            <th className="text-right px-4 py-2"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-lq-ink-100">
                        {c.students.length === 0 && (
                            <tr><td colSpan="6" className="px-4 py-4 text-lq-ink-500 text-center">No students yet. Click "Add student" above, or share the join code so children can self-register under your supervision.</td></tr>
                        )}
                        {c.students.map((s) => (
                            <tr key={s.id} data-testid={`student-${s.id}`} className="hover:bg-lq-purple-50/40">
                                <td className="px-4 py-2">
                                    <button onClick={() => setProgressStudent(s)} className="text-left font-medium text-lq-ink-900 hover:text-lq-purple-700 inline-flex items-center gap-1" data-testid={`student-name-${s.id}`}>
                                        {s.display_name} <ChevronRight size={12} className="text-lq-ink-500" />
                                    </button>
                                </td>
                                <td className="px-4 py-2 text-xs font-mono text-lq-ink-500">{s.student_username || '—'}</td>
                                <td className="px-4 py-2 text-xs">
                                    {(s.assigned_language_slugs || []).length === 0 ? (
                                        <span className="text-lq-ink-500">None yet</span>
                                    ) : (
                                        <div className="flex flex-wrap gap-1">
                                            {(s.assigned_language_slugs || []).map((slug) => {
                                                const ld = LAUNCH_LANGUAGES.find((l) => l.slug === slug);
                                                return (
                                                    <span key={slug} className="inline-block bg-lq-purple-100 text-lq-purple-700 px-2 py-0.5 rounded-full font-medium">
                                                        {ld ? `${ld.flag} ${ld.label}` : slug}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    )}
                                </td>
                                <td className="px-4 py-2">{s.progress_count}</td>
                                <td className="px-4 py-2 text-xs">{new Date(s.created_at).toLocaleDateString()}</td>
                                <td className="px-4 py-2 text-right">
                                    <button onClick={() => removeStudent(s)} className="text-xs text-lq-error hover:underline inline-flex items-center gap-1" data-testid={`remove-student-${s.id}`}>
                                        <Trash2 size={12} /> Remove
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {progressStudent && (
                <StudentProgressDrawer
                    student={progressStudent}
                    onClose={() => setProgressStudent(null)}
                    onLanguagesUpdated={onChange}
                />
            )}
        </section>
    );
}


function AddStudentForm({ classId, onClose, onCreated }) {
    const [displayName, setDisplayName] = useState('');
    const [username, setUsername] = useState('');
    const [pin, setPin] = useState('');
    const [languages, setLanguages] = useState([]);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    const toggleLanguage = (slug) => {
        setLanguages((cur) => {
            if (cur.includes(slug)) return cur.filter((s) => s !== slug);
            if (cur.length >= MAX_LANGUAGES) {
                toast.error(`A student can learn at most ${MAX_LANGUAGES} languages — deselect one first.`);
                return cur;
            }
            return [...cur, slug];
        });
    };

    const submit = async (e) => {
        e.preventDefault();
        setErr(null);
        setBusy(true);
        try {
            const { data } = await api.post('/teacher/students', {
                class_id: classId,
                display_name: displayName.trim(),
                student_username: username.trim(),
                pin: pin.trim(),
                language_slugs: languages,
            });
            toast.success(`Student "${data.student.display_name}" created — username ${data.student.student_username}`);
            await onCreated?.();
        } catch (e) {
            setErr(e?.response?.data?.detail || 'Could not create student');
        } finally { setBusy(false); }
    };

    return (
        <form onSubmit={submit} className="mt-5 bg-lq-purple-50/60 border border-lq-purple-100 rounded-2xl p-5" data-testid="add-student-form">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="font-display text-lg font-semibold text-lq-ink-900 inline-flex items-center gap-2"><Users size={18}/> Add a new student</h3>
                    <p className="text-xs text-lq-ink-500 mt-1">The student will sign in with their username and PIN at /schools/join (no email needed).</p>
                </div>
                <button type="button" onClick={onClose} className="text-lq-ink-500 hover:text-lq-ink-900" aria-label="Close"><XIcon size={18}/></button>
            </div>
            <div className="mt-4 grid sm:grid-cols-3 gap-3">
                <label className="block">
                    <span className="text-xs font-medium text-lq-ink-900">Display name *</span>
                    <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} required maxLength={40} placeholder="e.g. Aiyana" className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2" data-testid="new-student-display-name" />
                </label>
                <label className="block">
                    <span className="text-xs font-medium text-lq-ink-900">Username * (unique in this class)</span>
                    <input value={username} onChange={(e) => setUsername(e.target.value.replace(/[^A-Za-z0-9._-]/g, ''))} required minLength={3} maxLength={40} placeholder="e.g. aiyana-y6" className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2 font-mono" data-testid="new-student-username" />
                </label>
                <label className="block">
                    <span className="text-xs font-medium text-lq-ink-900">PIN * (4–10 chars)</span>
                    <input value={pin} onChange={(e) => setPin(e.target.value.replace(/[^A-Za-z0-9]/g, ''))} required minLength={4} maxLength={10} placeholder="e.g. 4242" className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2 font-mono" data-testid="new-student-pin" />
                </label>
            </div>
            <div className="mt-4">
                <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-lq-ink-900 inline-flex items-center gap-1"><Globe size={12}/> Languages this student will learn ({languages.length}/{MAX_LANGUAGES})</span>
                </div>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {LAUNCH_LANGUAGES.map((l) => {
                        const on = languages.includes(l.slug);
                        const disabled = !on && languages.length >= MAX_LANGUAGES;
                        return (
                            <button
                                type="button"
                                key={l.slug}
                                disabled={disabled}
                                onClick={() => toggleLanguage(l.slug)}
                                className={`text-sm px-3 py-2 rounded-xl border-2 transition-colors text-left ${on ? 'bg-lq-purple-500 text-white border-lq-purple-500' : disabled ? 'bg-white border-lq-ink-100 opacity-40' : 'bg-white border-lq-ink-300 hover:border-lq-purple-300'}`}
                                data-testid={`new-student-lang-${l.slug}`}
                            >
                                <span className="text-lg mr-1">{l.flag}</span>{l.label}
                            </button>
                        );
                    })}
                </div>
            </div>
            {err && <div className="mt-3 text-sm text-lq-error" data-testid="add-student-error">{err}</div>}
            <div className="mt-4 flex justify-end gap-2">
                <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-full bg-white border border-lq-ink-300 text-lq-ink-900 hover:bg-lq-ink-100">Cancel</button>
                <button type="submit" disabled={busy || !displayName.trim() || !username.trim() || !pin.trim()} className="px-5 py-2 text-sm font-semibold rounded-full bg-lq-purple-500 text-white hover:bg-lq-purple-700 disabled:opacity-50 inline-flex items-center gap-2" data-testid="new-student-submit">
                    {busy && <Loader2 size={14} className="animate-spin" />} Create student
                </button>
            </div>
        </form>
    );
}


function StudentProgressDrawer({ student, onClose, onLanguagesUpdated }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editingLangs, setEditingLangs] = useState(false);
    const [langs, setLangs] = useState(student.assigned_language_slugs || []);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const { data } = await api.get(`/teacher/students/${student.id}/progress`);
                if (mounted) setData(data);
            } catch (e) {
                toast.error(e?.response?.data?.detail || 'Could not load progress');
            } finally { if (mounted) setLoading(false); }
        })();
        return () => { mounted = false; };
    }, [student.id]);

    const saveLanguages = async () => {
        try {
            await api.patch(`/teacher/students/${student.id}/languages`, { language_slugs: langs });
            toast.success('Languages updated');
            setEditingLangs(false);
            await onLanguagesUpdated?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not update');
        }
    };

    const toggleLang = (slug) => {
        setLangs((cur) => {
            if (cur.includes(slug)) return cur.filter((s) => s !== slug);
            if (cur.length >= MAX_LANGUAGES) {
                toast.error(`Maximum ${MAX_LANGUAGES} languages.`);
                return cur;
            }
            return [...cur, slug];
        });
    };

    return (
        <div className="fixed inset-0 z-40 bg-lq-ink-black/40 flex justify-end" onClick={onClose} data-testid="student-progress-drawer">
            <div className="w-full max-w-xl bg-white h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="sticky top-0 z-10 bg-white border-b border-lq-ink-100 px-6 py-4 flex items-center justify-between">
                    <div>
                        <h3 className="font-display text-xl font-semibold text-lq-ink-900">{student.display_name}</h3>
                        <p className="text-xs text-lq-ink-500 font-mono">{student.student_username || '(no username)'} · joined {new Date(student.created_at).toLocaleDateString()}</p>
                    </div>
                    <button onClick={onClose} className="text-lq-ink-500 hover:text-lq-ink-900" aria-label="Close progress drawer" data-testid="close-progress-drawer">
                        <XIcon size={20}/>
                    </button>
                </div>

                <div className="px-6 py-5 space-y-6">
                    {loading && <div className="text-center py-10 text-lq-ink-500"><Loader2 className="inline-block animate-spin"/> Loading progress…</div>}
                    {!loading && data && (
                        <>
                            <div className="grid grid-cols-2 gap-3" data-testid="student-progress-stats">
                                <Stat label="Puzzles played" value={data.puzzles.total_plays} />
                                <Stat label="Quiz attempts" value={data.country_quiz.total_attempts} />
                                <Stat label="Quiz correct" value={`${data.country_quiz.correct} (${Math.round(data.country_quiz.accuracy * 100)}%)`} />
                                <Stat label="Quiz points" value={data.country_quiz.points_total} />
                                <Stat label="Words known" value={data.vocabulary_words_known} />
                                <Stat label="Last active" value={data.last_active_at ? new Date(data.last_active_at).toLocaleDateString() : 'Not yet'} />
                            </div>

                            <section>
                                <div className="flex items-center justify-between">
                                    <h4 className="font-display text-sm uppercase tracking-wider text-lq-ink-500">Assigned languages ({langs.length}/{MAX_LANGUAGES})</h4>
                                    {!editingLangs ? (
                                        <button onClick={() => setEditingLangs(true)} className="text-xs text-lq-purple-700 hover:underline" data-testid="edit-languages-btn">Edit</button>
                                    ) : (
                                        <div className="flex gap-2">
                                            <button onClick={() => { setLangs(student.assigned_language_slugs || []); setEditingLangs(false); }} className="text-xs text-lq-ink-500 hover:underline">Cancel</button>
                                            <button onClick={saveLanguages} className="text-xs font-semibold text-lq-purple-700 hover:underline" data-testid="save-languages-btn">Save</button>
                                        </div>
                                    )}
                                </div>
                                <div className="mt-2 flex flex-wrap gap-2" data-testid="student-languages-edit">
                                    {LAUNCH_LANGUAGES.map((l) => {
                                        const on = langs.includes(l.slug);
                                        return editingLangs ? (
                                            <button key={l.slug} onClick={() => toggleLang(l.slug)} className={`text-xs px-3 py-1.5 rounded-full border-2 ${on ? 'bg-lq-purple-500 text-white border-lq-purple-500' : 'bg-white border-lq-ink-300'}`} data-testid={`drawer-lang-${l.slug}`}>
                                                {l.flag} {l.label}
                                            </button>
                                        ) : on ? (
                                            <span key={l.slug} className="text-xs px-3 py-1.5 rounded-full bg-lq-purple-100 text-lq-purple-700 font-medium">{l.flag} {l.label}</span>
                                        ) : null;
                                    })}
                                    {!editingLangs && langs.length === 0 && <span className="text-xs text-lq-ink-500">None assigned yet.</span>}
                                </div>
                            </section>

                            <section data-testid="puzzle-breakdown">
                                <h4 className="font-display text-sm uppercase tracking-wider text-lq-ink-500 mb-2">Puzzle activity</h4>
                                {data.puzzles.by_puzzle.length === 0 ? (
                                    <p className="text-sm text-lq-ink-500">No puzzles played yet.</p>
                                ) : (
                                    <div className="bg-lq-paper border border-lq-ink-100 rounded-xl overflow-x-auto">
                                        <table className="w-full text-xs">
                                            <thead className="bg-lq-purple-50 text-lq-purple-900 uppercase tracking-wider">
                                                <tr>
                                                    <th className="text-left px-3 py-2">Language</th>
                                                    <th className="text-left px-3 py-2">Puzzle</th>
                                                    <th className="text-right px-3 py-2">Plays</th>
                                                    <th className="text-right px-3 py-2">Avg attempts</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-lq-ink-100">
                                                {data.puzzles.by_puzzle.map((p, i) => (
                                                    <tr key={`${p.language_slug}-${p.puzzle_type}-${i}`}>
                                                        <td className="px-3 py-1.5 capitalize">{p.language_slug || '—'}</td>
                                                        <td className="px-3 py-1.5 capitalize">{(p.puzzle_type || '').replace(/_/g, ' ')}</td>
                                                        <td className="px-3 py-1.5 text-right">{p.plays}</td>
                                                        <td className="px-3 py-1.5 text-right font-mono">{p.attempts_per_play}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </section>

                            <section data-testid="quiz-breakdown">
                                <h4 className="font-display text-sm uppercase tracking-wider text-lq-ink-500 mb-2">Country quiz</h4>
                                {data.country_quiz.by_country.length === 0 ? (
                                    <p className="text-sm text-lq-ink-500">No quizzes taken yet.</p>
                                ) : (
                                    <div className="bg-lq-paper border border-lq-ink-100 rounded-xl overflow-x-auto">
                                        <table className="w-full text-xs">
                                            <thead className="bg-lq-purple-50 text-lq-purple-900 uppercase tracking-wider">
                                                <tr>
                                                    <th className="text-left px-3 py-2">Country</th>
                                                    <th className="text-right px-3 py-2">Attempts</th>
                                                    <th className="text-right px-3 py-2">Correct</th>
                                                    <th className="text-right px-3 py-2">Accuracy</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-lq-ink-100">
                                                {data.country_quiz.by_country.map((q) => (
                                                    <tr key={q.country_slug}>
                                                        <td className="px-3 py-1.5 capitalize">{q.country_slug}</td>
                                                        <td className="px-3 py-1.5 text-right">{q.attempts}</td>
                                                        <td className="px-3 py-1.5 text-right">{q.correct}</td>
                                                        <td className="px-3 py-1.5 text-right">{Math.round(q.accuracy * 100)}%</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </section>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}


function Stat({ label, value }) {
    return (
        <div className="bg-lq-paper border border-lq-ink-100 rounded-xl px-4 py-3">
            <div className="text-xs uppercase tracking-wider text-lq-ink-500">{label}</div>
            <div className="font-display text-xl text-lq-ink-900 mt-0.5">{value}</div>
        </div>
    );
}


/** Class leaderboard — visible only on the teacher dashboard. Anonymised to
 * display name + points; designed to gently motivate the cohort without
 * exposing the ranking to students themselves. */
function ClassLeaderboard({ classId }) {
    const [data, setData] = useState(null);
    const [collapsed, setCollapsed] = useState(false);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        api.get(`/teacher/classes/${classId}/leaderboard`)
            .then(({ data }) => { if (mounted) { setData(data); setErr(null); } })
            .catch((e) => { if (mounted) setErr(e?.response?.data?.detail || 'Failed to load'); })
            .finally(() => { if (mounted) setLoading(false); });
        return () => { mounted = false; };
    }, [classId]);

    const top = (data?.students || []).slice(0, 5);

    return (
        <div className="mt-5 bg-gradient-to-br from-lq-peach-100 to-lq-purple-50 rounded-2xl border border-lq-peach-500/30 p-5" data-testid={`class-leaderboard-${classId}`}>
            <div className="flex items-center justify-between gap-3">
                <h3 className="font-display text-lg font-semibold text-lq-ink-900 inline-flex items-center gap-2">
                    <Trophy size={18} className="text-lq-peach-600"/> Class leaderboard
                    <span className="text-[10px] uppercase tracking-wider text-lq-ink-500 font-sans">teacher view only</span>
                </h3>
                <button type="button" onClick={() => setCollapsed((v) => !v)} className="text-xs text-lq-ink-500 hover:text-lq-ink-900" data-testid={`leaderboard-toggle-${classId}`}>
                    {collapsed ? 'Show' : 'Hide'}
                </button>
            </div>
            {!collapsed && (
                <div className="mt-3">
                    {loading && <div className="text-sm text-lq-ink-500"><Loader2 className="inline-block animate-spin" size={14}/> Tallying scores…</div>}
                    {err && <div className="text-sm text-lq-error">{err}</div>}
                    {!loading && !err && top.length === 0 && (
                        <p className="text-sm text-lq-ink-500">No activity yet — once students complete puzzles or quizzes their scores will rank here.</p>
                    )}
                    {!loading && !err && top.length > 0 && (
                        <ol className="space-y-1.5">
                            {top.map((s) => {
                                const medal = s.rank === 1 ? '🥇' : s.rank === 2 ? '🥈' : s.rank === 3 ? '🥉' : `#${s.rank}`;
                                return (
                                    <li key={s.student_id} className="flex items-center justify-between bg-white/80 rounded-xl px-3 py-2 text-sm" data-testid={`leaderboard-row-${s.student_id}`}>
                                        <div className="flex items-center gap-2 min-w-0">
                                            <span className="text-base w-7 text-center">{medal}</span>
                                            <span className="font-semibold text-lq-ink-900 truncate">{s.display_name}</span>
                                            <span className="text-[10px] text-lq-ink-500 hidden sm:inline">{(s.languages || []).join(' · ')}</span>
                                        </div>
                                        <div className="text-right">
                                            <span className="font-mono font-semibold text-lq-purple-700">{s.points.toLocaleString()}</span>
                                            <span className="text-[10px] text-lq-ink-500 ml-1">pts</span>
                                            <span className="text-[10px] text-lq-ink-500 ml-2">{s.plays} plays</span>
                                        </div>
                                    </li>
                                );
                            })}
                        </ol>
                    )}
                </div>
            )}
        </div>
    );
}
